// Library Methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";
import Alert from "src/components/shared/alerts/Alert/Alert";
import IOLDescription from "./IOLDescription/IOLDescription";
import AddEditIOLModal from "./AddEditIOLModal/AddEditIOLModal";
import DeleteIOLModal from "./DeleteIOLModal/DeleteIOLModal";
import { PlusIcon } from "src/assets/icons";

// Types
import { IOLLensType } from "src/services/clinics/types";

// Styles
import {
  ModalCustomConfirmDarkCta,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

// Hooks
import { useIOLLenses } from "src/services/clinics/hooks/clinic-lenses/iol-lenses/useIOLLenses";
import { useAddIOLLens } from "src/services/clinics/hooks/clinic-lenses/iol-lenses/useAddIOLLens";
import { useUpdateIOLLens } from "src/services/clinics/hooks/clinic-lenses/iol-lenses/useUpdateIOLLens";
import { useDeleteIOLLens } from "src/services/clinics/hooks/clinic-lenses/iol-lenses/useDeleteIOLLens";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type IOLSettingsType = {
  isLoadingSettings: boolean;
  isUpdatingSettings: boolean;
};

const IOLSettings = ({
  isLoadingSettings,
  isUpdatingSettings,
}: IOLSettingsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const [selectedLens, setSelectedLens] = useState<IOLLensType>();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const {
    data: lensesData,
    isLoading: isLoadingLenses,
    isError: isErrorLenses,
    isFetching: isFetchingLenses,
    isRefetching: isRefetchingLenses,
  } = useIOLLenses(clinicInfo?.id);

  const { mutate: addLensMutate, isPending: addLensPending } = useAddIOLLens(
    clinicInfo?.id
  );
  const { mutate: updateLensMutate, isPending: updateLensPending } =
    useUpdateIOLLens(clinicInfo?.id);
  const { mutate: deleteLensMutate, isPending: deleteLensPending } =
    useDeleteIOLLens(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingSettings || isLoadingLenses,
    [isLoadingSettings, isLoadingLenses]
  );

  const isUpdating = useMemo(
    () =>
      isUpdatingSettings ||
      isFetchingLenses ||
      isRefetchingLenses ||
      addLensPending ||
      updateLensPending ||
      deleteLensPending,
    [
      isUpdatingSettings,
      isFetchingLenses,
      isRefetchingLenses,
      addLensPending,
      updateLensPending,
      deleteLensPending,
    ]
  );

  const hasLenses = useMemo(
    () =>
      !isLoadingLenses &&
      !isErrorLenses &&
      lensesData &&
      lensesData?.length > 0,
    [isLoadingLenses, isErrorLenses, lensesData]
  );

  const hasNoLenses = useMemo(
    () => !isLoadingLenses && !isErrorLenses && !lensesData?.length,
    [isLoadingLenses, isErrorLenses, lensesData]
  );

  const addLens = useCallback(
    (lensData: { name: string; lensType: string; price: string }) => {
      addLensMutate(lensData, { onSuccess: () => setOpenAddModal(false) });
    },
    [addLensMutate]
  );

  const updateLens = useCallback(
    (lensData: { name: string; lensType: string; price: string }) => {
      if (!selectedLens) return;

      updateLensMutate(
        { ...selectedLens, ...lensData },
        {
          onSuccess: () => {
            setSelectedLens(undefined);
            setOpenEditModal(false);
          },
        }
      );
    },
    [updateLensMutate, selectedLens]
  );

  const deleteLens = useCallback(() => {
    if (!selectedLens) return;

    deleteLensMutate(selectedLens?.id, {
      onSuccess: () => {
        setSelectedLens(undefined);
        setOpenDeleteModal(false);
      },
    });
  }, [deleteLensMutate, selectedLens]);

  return (
    <>
      <DefaultCard>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" flexDirection="column" gap="16px">
            <Box display="flex" justifyContent="space-between" gap="16px">
              <Box display="flex" flexDirection="column" gap="6px">
                <Typography fontSize="14px" fontWeight={600}>
                  {t("IOL Options")}
                </Typography>

                <Typography fontSize="14px" color={mutedText}>
                  {t(
                    "Manage virtual lenses that can be used in lens simulations."
                  )}
                </Typography>
              </Box>
              <Button
                disabled={isLoading || isUpdating}
                sx={ModalCustomConfirmDarkCta}
                startIcon={<PlusIcon />}
                onClick={() => setOpenAddModal(true)}
              >
                {t("Add")}
              </Button>
            </Box>

            {isLoading ? (
              <Box display="flex" flexDirection="column" gap="20px">
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={UI_SIZES.inputHeight}
                  sx={{
                    borderRadius: "4px",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={90}
                  sx={{
                    borderRadius: "4px",
                  }}
                />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap="20px">
                {hasNoLenses && (
                  <Alert
                    variant="warning"
                    alertContent={t("No lens options found.")}
                  />
                )}

                {hasLenses && (
                  <Box display="flex" flexWrap="wrap" gap="12px">
                    {lensesData?.map((lens, index) => (
                      <Box
                        key={`${lens.name}-${index}`}
                        sx={{
                          flexGrow: 1,
                          flexBasis: {
                            xs: "100%",
                            md: "40%",
                          },
                          minWidth: 0,
                          "& > *": { height: "100%" },
                        }}
                      >
                        <IOLDescription
                          isUpdating={isUpdating}
                          lensData={lens}
                          openDeleteModal={() => {
                            setSelectedLens(lens);
                            setOpenDeleteModal(true);
                          }}
                          openEditModal={() => {
                            setSelectedLens(lens);
                            setOpenEditModal(true);
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DefaultCard>

      <AddEditIOLModal
        open={openAddModal}
        title={t("Add IOL Option")}
        onClose={() => setOpenAddModal(false)}
        onConfirm={addLens}
        isUpdating={isUpdating}
      />

      <AddEditIOLModal
        open={openEditModal}
        title={t("Edit IOL Option")}
        lensToEdit={selectedLens}
        onClose={() => {
          setSelectedLens(undefined);
          setOpenEditModal(false);
        }}
        onConfirm={updateLens}
        isUpdating={isUpdating}
      />

      <DeleteIOLModal
        isLoading={isUpdating}
        selectedLens={selectedLens}
        open={openDeleteModal}
        onClose={() => {
          setSelectedLens(undefined);
          setOpenDeleteModal(false);
        }}
        onConfirm={deleteLens}
      />
    </>
  );
};

export default IOLSettings;
